import { TranslationsContext } from '@teliaee/telia-translate';
import { IReactionDisposer, observable, reaction } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { AnalyticsProvider, Heading, Tabs, TabsItem, type THeadingLevel } from 'telia-front-react';
import { SEGMENT_PRIVATE } from '../stores/SegmentStore';
import {
  calculateMonthlyPaymentForCapitalRent,
  calculateMonthlyPaymentForUsageRent,
  defaultCapitalRentProps,
  defaultUsageRentProps,
  getStateFromRentProps
} from './PostpayCalculatorUtil';
import { RentCalculatorState, PostpayCalculatorProps, PostpayCalculatorsState } from './PostpayInterfaces';

import RentCalculator from './RentCalculator';

@observer
class PostpayCalculator extends React.Component<PostpayCalculatorProps, PostpayCalculatorsState> {
  static contextType = TranslationsContext;
  declare context: React.ContextType<typeof TranslationsContext>;
  private disposers: IReactionDisposer[];
  @observable accessor customerId: number;
  @observable accessor isEmployee: boolean;

  static defaultProps: PostpayCalculatorProps = {
    usageRentProps: defaultUsageRentProps,
    capitalRentProps: defaultCapitalRentProps,
    ...defaultCapitalRentProps,
    interestRate: '21,9',
    contractFee: '19,9'
  };

  public constructor(props: PostpayCalculatorProps) {
    super(props);
    const { capitalRentProps, usageRentProps, ssoStore, segmentStore } = props;
    const instalmentProps = { ...props };
    if (ssoStore.isEmployee) {
      instalmentProps.interestRate = '15,9';
      instalmentProps.contractFee = '9,9';
    }
    this.customerId = ssoStore.customerId;
    this.isEmployee = ssoStore.isEmployee;

    this.state = {
      capitalRentState: getStateFromRentProps(segmentStore.currentSegment === SEGMENT_PRIVATE ? instalmentProps : capitalRentProps),
      usageRentState: getStateFromRentProps(usageRentProps)
    };
    this.disposers = [
      reaction(
        () => this.customerId,
        () => ssoStore.fetchIsEmployee(),
        { fireImmediately: true }
      ),
      reaction(
        () => this.isEmployee,
        () => {
          this.updateCapitalRentState({
            interestRate: this.isEmployee ? '15,9' : props.interestRate || '21,9',
            contractFee: this.isEmployee ? '9,9' : props.contractFee || '19,9'
          });
        }
      )
    ];
  }

  public componentDidUpdate(prevProps: Readonly<PostpayCalculatorProps>) {
    const { customerId: prevCustomerId, isEmployee: prevIsEmployee } = prevProps.ssoStore;
    const { customerId: newCustomerId, isEmployee: newIsEmployee } = this.props.ssoStore;
    if (prevCustomerId !== newCustomerId) {
      this.customerId = newCustomerId;
    }
    if (prevIsEmployee !== newIsEmployee) {
      this.isEmployee = newIsEmployee;
    }
  }

  public componentWillUnmount() {
    this.disposers?.forEach((dispose) => dispose());
  }

  private updateUsageRentState = (statePartial: Partial<RentCalculatorState>) => {
    const newState: RentCalculatorState = { ...this.state.usageRentState, ...(statePartial as RentCalculatorState) };
    this.setState((state) => ({ ...state, usageRentState: newState }));
  };

  private updateCapitalRentState = (statePartial: Partial<RentCalculatorState>) => {
    const newState: RentCalculatorState = { ...this.state.capitalRentState, ...(statePartial as RentCalculatorState) };
    this.setState((state) => ({ ...state, capitalRentState: newState }));
  };

  public render(): JSX.Element {
    const translate = this.context.translate;
    return (
      <>
        <Heading level={this.props.headingLevel} id="postpay-calculators" className="sr-only">
          {translate('postpayCalculator.heading')}
        </Heading>
        {this.props.segmentStore.currentSegment === SEGMENT_PRIVATE ? (
          <AnalyticsProvider group="Järelmaksu kalkulaator">
            <RentCalculator
              {...this.props}
              handleStateChange={this.updateCapitalRentState}
              state={this.state.capitalRentState}
              calculateMonthlyPayment={calculateMonthlyPaymentForCapitalRent}
            />
          </AnalyticsProvider>
        ) : (
          <Tabs
            defaultCurrentTab="tab-item-usagerent"
            size="huge"
            border="none"
            aria-labelledby="postpay-calculators"
            headingLevel={(this.props.headingLevel + 1) as THeadingLevel}
          >
            <TabsItem id="tab-item-usagerent" label={translate('postpayCalculator.usageRent')}>
              <AnalyticsProvider group="Kasutusrendi kalkulaator">
                <RentCalculator
                  {...this.props.usageRentProps}
                  handleStateChange={this.updateUsageRentState}
                  state={this.state.usageRentState}
                  calculateMonthlyPayment={calculateMonthlyPaymentForUsageRent}
                  showBuyback={true}
                />
              </AnalyticsProvider>
            </TabsItem>
            <TabsItem id="tab-item-capitalrent" label={translate('postpayCalculator.capitalRent')}>
              <AnalyticsProvider group="Kapitalirendi kalkulaator">
                <RentCalculator
                  {...this.props.capitalRentProps}
                  handleStateChange={this.updateCapitalRentState}
                  state={this.state.capitalRentState}
                  calculateMonthlyPayment={calculateMonthlyPaymentForCapitalRent}
                />
              </AnalyticsProvider>
            </TabsItem>
          </Tabs>
        )}
      </>
    );
  }
}

export default inject('ssoStore', 'segmentStore')(PostpayCalculator);
